import _ from "lodash";
import dayjs from "dayjs";
import { Line, type LineConfig } from "@ant-design/plots";
import { AppCard } from "@lib/components/AppComponents";
import { Empty, Skeleton } from "antd";
import { useIntl } from "react-intl";
import { PaginationType, UseQueryProps } from "@lib/types/Api";
import { AILog } from "@lib/types/AILog";
import { useTheme } from "styled-components";
import { useMemo } from "react";
import { currencySymbol } from "@lib/helpers/StringHelper";
import { CurrencyEnum } from "@lib/types/Transaction";

type Props = {
  query?: UseQueryProps<PaginationType<AILog>>;
  extra?: string | JSX.Element;
  navigate?: () => void;
  loading?: boolean;
  refetch?: () => void;
};

const AITaskDailyUsageChart = (props: Props) => {
  const { messages } = useIntl();
  const { mode } = useTheme();

  const { query } = props;

  const { data, isLoading, isFetching } = query || {};
  const apiData = data?.data?.data || [];
  const chartData = useMemo(() => {
    if (!apiData || apiData.length === 0) return [];

    // Find all unique tasks
    const allTasks = _.uniq(apiData.map((item) => item.task || "Unknown"));

    // Find min and max dates
    const dates = apiData.map((item) => dayjs(item.createdAt).startOf("day"));
    const timestamps = dates.map((d) => d.valueOf());
    const minDate = dayjs(Math.min(...timestamps));
    const maxDate = dayjs(Math.max(...timestamps));

    // Generate all dates in range
    const allDates: dayjs.Dayjs[] = [];
    let currentDate = minDate.startOf("day");
    while (
      currentDate.isBefore(maxDate, "day") ||
      currentDate.isSame(maxDate, "day")
    ) {
      allDates.push(currentDate);
      currentDate = currentDate.add(1, "day");
    }

    // Group by date and series, then sum the costs
    const grouped = _.groupBy(apiData, (item) => {
      const date = dayjs(item.createdAt).format("DD/MM/YYYY");
      const series = item.task || "Unknown";
      return `${date}_${series}`;
    });

    // Create a map for quick lookup
    const dataMap = new Map<string, number>();
    Object.entries(grouped).forEach(([key, items]) => {
      const totalCost = _.sumBy(items, (item) => item.cost || 0);
      dataMap.set(key, _.round(totalCost, 3));
    });

    // Generate data for all date-task combinations
    const data: Array<{
      date: string;
      series: string;
      value: number;
      title: string;
      dateValue: number;
    }> = [];

    allDates.forEach((date) => {
      const dateStr = date.format("DD/MM/YYYY");
      allTasks.forEach((task) => {
        const key = `${dateStr}_${task}`;
        const value = dataMap.get(key) || 0;

        data.push({
          date: dateStr,
          series: task,
          value,
          title: `${value} ${currencySymbol(CurrencyEnum.USD)}`,
          dateValue: date.valueOf(),
        });
      });
    });

    // Sort by date ascending (oldest to newest) since API returns DESC order
    return data.sort((a, b) => a.dateValue - b.dateValue);
  }, [apiData]);

  const config: LineConfig = {
    theme: {
      type: mode,
    },
    data: chartData,

    colorField: "series",
    yField: "value",
    xField: "date",
    axis: {
      x: {
        labelAutoRotate: false,
        title: messages["common.date"] as string,
        labelFontSize: 12,
        labelFormatter: (d) => `${d}`,
      },
      y: {
        title: messages["common.cost"] as string,
        labelFontSize: 12,
        labelFormatter: (d) => `${d} ${currencySymbol(CurrencyEnum.USD)}`,
      },
    },

    legend: {
      color: {
        itemMarker: "rect",
        itemMarkerSize: 20,
        itemMarkerLineWidth: 2,
        itemMarkerShadowOffsetX: 10,
        itemMarkerShadowOffsetY: 10,
      },
    },

    tooltip: {
      items: [
        (datum, index, data, column) => ({
          value: `${column.y.value[index]} ${currencySymbol(CurrencyEnum.USD)}`,
        }),
      ],
    },

    autoFit: true,
    slider: {
      x: {
        labelFormatter: (d) => `${dayjs(d).format("DD/MM/YYYY")}`,
      },
      y: {
        labelFormatter: (d) => `${d} ${currencySymbol(CurrencyEnum.USD)}`,
      },
    },
  };

  return (
    <AppCard title={messages["common.aiTaskDailyUsageChart"] as string}>
      {isLoading || isFetching ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <>
          {chartData.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <Empty />
            </div>
          ) : (
            <Line {...config} />
          )}
        </>
      )}
    </AppCard>
  );
};

export default AITaskDailyUsageChart;
